import { useEffect, useMemo, useRef } from 'react';

const DEFAULT_EVENTS = [
    'mousemove', 'mousedown', 'click', 'scroll',
    'wheel', 'keydown', 'touchstart', 'pointerdown',
];
const ACTIVITY_THROTTLE_MS = 1000;
const DEBUG_IDLE_MS = 10000;

export default function useIdleCallback({ enabled = true, idleMs = 300000, onIdle, activityEvents }) {
    const onIdleRef = useRef(onIdle);
    useEffect(() => { onIdleRef.current = onIdle; }, [onIdle]);

    const eventsRef = useRef(activityEvents || DEFAULT_EVENTS);

    const debugIdleRefresh = useMemo(() => {
        try {
            return new URLSearchParams(window.location.search).get('debugIdleRefresh') === '1';
        } catch { return false; }
    }, []);

    const effectiveIdleMs = debugIdleRefresh ? DEBUG_IDLE_MS : idleMs;

    useEffect(() => {
        if (!enabled) return;

        let timer = null;
        let lastActivity = 0;
        const events = eventsRef.current;

        const arm = () => {
            if (timer) clearTimeout(timer);
            timer = setTimeout(() => {
                if (debugIdleRefresh) console.debug('[IdleRefresh] idle timeout fired');
                onIdleRef.current();
                arm();
            }, effectiveIdleMs);
        };

        const onActivity = () => {
            const now = Date.now();
            if (now - lastActivity < ACTIVITY_THROTTLE_MS) return;
            lastActivity = now;
            if (debugIdleRefresh) console.debug('[IdleRefresh] activity detected, timer reset');
            arm();
        };

        if (debugIdleRefresh) console.debug(`[IdleRefresh] idle timer armed (${effectiveIdleMs}ms)`);
        arm();

        events.forEach(evt => window.addEventListener(evt, onActivity, { passive: true }));

        return () => {
            if (timer) clearTimeout(timer);
            events.forEach(evt => window.removeEventListener(evt, onActivity));
            if (debugIdleRefresh) console.debug('[IdleRefresh] cleanup, listeners removed');
        };
    }, [enabled, effectiveIdleMs, debugIdleRefresh]);

    return { debugIdleRefresh };
}
